import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { submitCallManagerFurtherReviewResolved } from "model/CallModel";
import { commonReasonsForAll } from "model/ReasonModel";
import { mapReasonMasterFilterRowsToSelectOptions } from "helpers/callDetailsCallNotesFieldRules";

const reactSelectMenuPortalStyles = {
  menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
  menu: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

const RESOLVED_STATUS_OPTIONS = [
  { value: "eligible", label: "Eligible" },
  { value: "not interested", label: "Not Interested" },
];

const NOT_INTERESTED_STATUS = "not interested";

const isNotInterestedStatus = (statusValue) =>
  String(statusValue ?? "").trim().toLowerCase() === NOT_INTERESTED_STATUS;

const resolveFurtherReviewRowId = (row = {}) =>
  String(row?.id ?? row?.Id ?? row?._id ?? "").trim();

const extractFurtherReviewPatchFromApiResponse = (response = {}) => {
  const responseData = response?.data;
  if (!responseData || typeof responseData !== "object") return {};
  if (Array.isArray(responseData)) {
    const firstRecord = responseData[0];
    return firstRecord && typeof firstRecord === "object" ? firstRecord : {};
  }
  if (responseData.record && typeof responseData.record === "object") {
    return responseData.record;
  }
  if (Array.isArray(responseData.records) && responseData.records[0]) {
    return responseData.records[0];
  }
  return responseData;
};

const FurtherReviewResolvedModal = ({ isOpen, onClose, row = {}, onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedStatus(null);
    setSelectedReason(null);
    setErrors({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let requestCancelled = false;
    (async () => {
      setReasonsLoading(true);
      try {
        const reasonsResponse = await commonReasonsForAll({});
        if (requestCancelled) return;
        const reasonRows = Array.isArray(reasonsResponse?.data)
          ? reasonsResponse.data
          : [];
        setReasonOptions(mapReasonMasterFilterRowsToSelectOptions(reasonRows));
      } catch {
        if (!requestCancelled) setReasonOptions([]);
      } finally {
        if (!requestCancelled) setReasonsLoading(false);
      }
    })();
    return () => {
      requestCancelled = true;
    };
  }, [isOpen]);

  const showReasonField = isNotInterestedStatus(selectedStatus?.value);

  const handleStatusChange = (option) => {
    setSelectedStatus(option);
    if (!isNotInterestedStatus(option?.value)) {
      setSelectedReason(null);
    }
    setErrors((prev) => ({
      ...prev,
      status: option?.value ? "" : prev.status,
      reason: isNotInterestedStatus(option?.value) ? prev.reason : "",
    }));
  };

  const handleReasonChange = (option) => {
    setSelectedReason(option);
    if (option?.value) {
      setErrors((prev) => ({ ...prev, reason: "" }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const statusValue = String(selectedStatus?.value ?? "").trim();
    const requiresReason = isNotInterestedStatus(statusValue);
    const reasonsId = requiresReason
      ? String(selectedReason?.value ?? "").trim()
      : null;
    const nextErrors = {};
    if (!statusValue) nextErrors.status = "Status is required";
    if (requiresReason && !reasonsId) nextErrors.reason = "Reason is required";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const recordId = resolveFurtherReviewRowId(row);
    if (!recordId) {
      toast.error("Record id is missing");
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitCallManagerFurtherReviewResolved(
        recordId,
        statusValue,
        reasonsId
      );
      if (response == null || response?.success === false) {
        if (response?.message) toast.error(response.message);
        return;
      }
      toast.success(response?.message || "Candidate resolved");
      const patchFromApi = extractFurtherReviewPatchFromApiResponse(response);
      onSuccess?.({
        ...patchFromApi,
        id: patchFromApi?.id ?? recordId,
        orderStatus: patchFromApi?.orderStatus ?? statusValue,
        status: patchFromApi?.status ?? patchFromApi?.orderStatus ?? statusValue,
        reasonsId:
          patchFromApi?.reasonsId !== undefined
            ? patchFromApi.reasonsId
            : reasonsId,
      });
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  const candidateLabel =
    String(row?.candidateName ?? row?.name ?? "").trim() || "Candidate";

  return (
    <Modal isOpen={isOpen} toggle={closeModal} backdrop="static" centered size="md" className="modal-full-height">
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">Resolved</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          <p className="text-muted small mb-3">{candidateLabel}</p>

          <FormGroup className={showReasonField ? "" : "mb-0"}>
            <Label>
              Status <span className="text-danger">*</span>
            </Label>
            <Select
              className="react-select-container select2-selection"
              classNamePrefix="react-select"
              options={RESOLVED_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={handleStatusChange}
              placeholder="Select status"
              isDisabled={submitting}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={reactSelectMenuPortalStyles}
              menuPosition="fixed"
            />
            {errors.status ? (
              <small className="text-danger d-block mt-1">{errors.status}</small>
            ) : null}
          </FormGroup>

          {showReasonField ? (
            <FormGroup className="mb-0">
              <Label>
                Reason <span className="text-danger">*</span>
              </Label>
              <Select
                className="react-select-container select2-selection"
                classNamePrefix="react-select"
                options={reasonOptions}
                value={selectedReason}
                onChange={handleReasonChange}
                isLoading={reasonsLoading}
                isDisabled={submitting || reasonsLoading}
                placeholder="Select reason"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={reactSelectMenuPortalStyles}
                menuPosition="fixed"
              />
              {errors.reason ? (
                <small className="text-danger d-block mt-1">{errors.reason}</small>
              ) : null}
            </FormGroup>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

FurtherReviewResolvedModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  row: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default FurtherReviewResolvedModal;
